import {useGlobalization} from '@planday-corp/globalization';
import IconButton from '@planday-corp/ui/actions/IconButton';
import Blurrable from '@planday-corp/ui/behaviour/Blurrable';
import TextInput from '@planday-corp/ui/forms/inputs/TextInput';
import colors, {Color, grey20} from '@planday-corp/ui/styles/colors';
import {useEffect, useRef, useState} from 'react';
import {styled} from 'styled-components';
import {teamLabels} from '~/Signup/translations';
import {removeExtraSpaces} from '~/Signup/utils';
import {LABELS} from '~/shared/labels/Labels';

const StyledPillInputWrappper = styled.div`
  display: flex;
  flex-direction: row;
  background-color: white;
  align-items: center;
  border-radius: 0.7rem;
`;

export const StyledPillInput = styled(TextInput)<{
  $initialWidth: number;
  $customWidth?: number;
}>`
  border-width: 2px !important;
  border-style: solid;
  border-color: ${grey20};
  line-height: calc(2rem - 4px);
  border-radius: 0.7rem 0 0 0.7rem;
  border-right: none !important;
  transition: width 0.25s ease-in-out;
  width: ${props => (props.$customWidth ? `${props.$customWidth}rem` : '100%')};
  height: initial;
  padding: 0 0.625rem;

  &&:hover {
    border-color: ${colors.blue50};
  }

  &&:focus {
    outline: none; */

    width: ${props => (props.$initialWidth >= 12.5 ? '100%' : '12.5rem')};
  }

  &&::placeholder {
    color: ${colors.blue50};
  }

  &&:focus::placeholder {
    color: transparent;
  }
`;

export const StyledIconButton = styled(IconButton)<{$color: Color}>`
  border: 2px solid ${grey20};
  border-radius: 0 0.7rem 0.7rem 0;
  border-left: 0;
  svg {
    fill: ${props => props.$color};
  }
`;

export const StyledIconButtonWrapper = styled.div`
  width: 2.25rem;
`;

interface EditableTextPillButtonProps {
  onChange: (value: string) => void;
}
export default function EditableTextPillButton({
  onChange,
}: EditableTextPillButtonProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const {i18n} = useGlobalization();

  const [isInputFocused, setIsInputFocused] = useState(false);
  const [initialInputWidth, setInitialInputWidth] = useState(0);

  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    if (inputRef.current) {
      const pixelWidth = inputRef.current.offsetWidth;
      const baseFontSize = parseFloat(
        getComputedStyle(inputRef.current).fontSize
      );
      const widthInRem = pixelWidth / baseFontSize;
      setInitialInputWidth(widthInRem);
    }
  }, []);

  const customInputWidth =
    inputValue !== '' && initialInputWidth <= 12.5 ? 12.5 : undefined;

  const inputPlaceholderText = i18n.t(teamLabels.addRole);

  const handleAddNewGroup = (value: string) => {
    const trimmedValue = removeExtraSpaces(value);
    onChange(trimmedValue);

    if (inputRef.current) {
      inputRef.current.focus();
    }
  };
  return (
    <Blurrable
      onClickOutside={() => {
        setIsInputFocused(false);
        setInputValue('');
      }}
    >
      {ref => (
        <StyledPillInputWrappper ref={ref}>
          <StyledPillInput
            ref={inputRef}
            aria-label={inputPlaceholderText}
            $customWidth={customInputWidth}
            $initialWidth={initialInputWidth}
            size={inputPlaceholderText.length}
            placeholder={inputPlaceholderText}
            value={inputValue}
            onChange={setInputValue}
            onKeyDown={e =>
              e.key === 'Enter' ? handleAddNewGroup(inputValue) : undefined
            }
            onFocus={() => setIsInputFocused(true)}
          />
          <StyledIconButtonWrapper>
            <StyledIconButton
              $color={
                isInputFocused || inputValue !== ''
                  ? (colors.grey80 as Color)
                  : (colors.blue50 as Color)
              }
              iconName={
                isInputFocused || inputValue !== '' ? 'plusLarge' : 'pencil'
              }
              onClick={() => {
                handleAddNewGroup(inputValue);
              }}
              title={LABELS.add}
              //@ts-ignore
              onBlur={() => {
                if (inputValue !== '') {
                  setInputValue('');
                }
              }}
              type="basic"
            />
          </StyledIconButtonWrapper>
        </StyledPillInputWrappper>
      )}
    </Blurrable>
  );
}
